/**
 * Site-wide theme loader.
 *
 * Fetches `/api/site/theme` once on mount and applies the active phosphor
 * and cabinet selections as classes on <body>. Room backgrounds are retired.
 * re-applies whenever the owner updates the theme via OwnerBar. Listens to
 * the `crt:theme-updated` window event so the picker can broadcast its
 * change without forcing a network round-trip.
 *
 * The CSS variables consumed by the rest of the codebase live in
 * `index.css`; this module is purely an applier — it owns no styling.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const PHOSPHOR_CLASSES = ["theme-amber", "theme-blue", "theme-white"];
const SKIN_CLASSES = ["skin-obsidian", "skin-pearl", "skin-brushed-steel", "skin-neon"];

// Snapshot of the owner-side theme persisted across reloads so the inline
// bootstrap script in public/index.html can paint the correct body classes
// BEFORE React mounts. Without this the page flashes the CSS default
// (green phosphor + memphis wallpaper + wood cabinet) for ~100-300ms while
// /api/site/theme is in flight, regardless of what the owner selected.
const OWNER_THEME_CACHE_KEY = "crt_owner_theme_v1";

function cacheOwnerTheme(theme) {
  if (!theme || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(OWNER_THEME_CACHE_KEY, JSON.stringify({
      phosphor: theme.phosphor || "green",
      skin: theme.skin || "wood",
    }));
  } catch (e) { /* quota / private-mode — noop */ }
}

function applyToBody(theme) {
  if (!theme || typeof document === "undefined") return;
  const body = document.body;
  for (const c of [...PHOSPHOR_CLASSES, ...SKIN_CLASSES]) body.classList.remove(c);
  // Phase 2.3.84 — station theme is authoritative. Viewer phosphor and
  // cabinet overrides have been retired; any legacy saved values are inert.
  const effectivePhosphor = theme.phosphor || "green";
  const effectiveSkin = theme.skin || "wood";
  if (effectivePhosphor && effectivePhosphor !== "green") body.classList.add(`theme-${effectivePhosphor}`);
  if (effectiveSkin && effectiveSkin !== "wood") body.classList.add(`skin-${effectiveSkin}`);
  // Persist the owner phosphor/skin side of the resolution.
  cacheOwnerTheme(theme);
}

export function useTheme() {
  const [theme, setTheme] = useState({
    phosphor: "green",
    skin: "wood",
    room: "memphis",
    brand_text: "SCARTLEAD.TV",
    callsign: "CRT-1986",
    active_season: "none",
  });

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/site/theme`);
      const next = r.data || {};
      setTheme((prev) => ({ ...prev, ...next }));
      applyToBody(next);
    } catch (e) {
      // Network fail → keep defaults; the CSS :root vars handle this gracefully.
    }
  }, []);

  useEffect(() => {
    reload();
    const onUpdate = (e) => {
      if (e && e.detail) {
        setTheme((prev) => ({ ...prev, ...e.detail }));
        applyToBody(e.detail);
      } else {
        reload();
      }
    };
    window.addEventListener("crt:theme-updated", onUpdate);
    return () => {
      window.removeEventListener("crt:theme-updated", onUpdate);
    };
  }, [reload]);

  return { theme, reload };
}

/** Imperative emitter used by the OwnerBar theme picker after a successful POST. */
export function broadcastThemeUpdated(next) {
  window.dispatchEvent(new CustomEvent("crt:theme-updated", { detail: next }));
}
